import { ChevronDown, ChevronRight, ChevronUp } from '../../ui/icons';
import { signedPoints, ordinalSuffix } from '../../../lib/format';
import { movementVerdictCopy } from '../../../lib/divisionExplain';
import { groupNumber } from '../../../lib/round';
import type { SeasonRoundResult } from '../../../hooks/useQueries';
import { cn } from '../../../lib/cn';

/** Settled rounds in one grouped card — compact rows with a movement accent bar. */
export function RoundHistoryList({
  results,
  onOpen,
  hideHeader = false
}: {
  results: SeasonRoundResult[];
  onOpen?: (roundId: string) => void;
  /** When embedded in a parent Section that already supplies the title. */
  hideHeader?: boolean;
}) {
  return (
    <div className={hideHeader ? undefined : 'pt-1'}>
      {!hideHeader && (
        <div className="mb-2 flex items-baseline justify-between px-0.5">
          <span className="text-[10px] font-extrabold uppercase tracking-[0.1em] text-ink3">Round history</span>
          <span className="text-[10px] font-semibold text-ink4">{results.length} settled</span>
        </div>
      )}

      <div className="overflow-hidden rounded-[18px] border border-black/10 bg-white shadow-tile">
        {results.map((result, i) => (
          <RoundHistoryRow
            key={result.round.roundId}
            result={result}
            onOpen={onOpen}
            last={i === results.length - 1}
          />
        ))}
      </div>
    </div>
  );
}

function RoundHistoryRow({
  result,
  onOpen,
  last
}: {
  result: SeasonRoundResult;
  onOpen?: (roundId: string) => void;
  last: boolean;
}) {
  const { round, entry, divisionSize } = result;
  const promoted = entry.movement === 'PROMOTED';
  const relegated = entry.movement === 'RELEGATED';
  const movementLabel = promoted ? 'Promoted' : relegated ? 'Relegated' : 'Held';
  const tappable = !!onOpen;
  const rank = entry.rankInGroup;
  const size = divisionSize ?? rank ?? 0;
  const groupLabel = entry.groupId ? `Group ${groupNumber(entry.groupId)}` : null;
  const why =
    rank != null && size > 0
      ? movementVerdictCopy({ rank, divisionSize: Math.max(size, rank), movement: entry.movement ?? 'STAYED' }).why
      : promoted
        ? 'Finished in the promotion zone'
        : relegated
          ? 'Finished in the drop zone'
          : null;

  const accent = promoted ? 'bg-accent' : relegated ? 'bg-miss' : 'bg-[#D4D4DC]';
  const scorePositive = (entry.roundScore ?? 0) > 0;

  const shell = cn(
    'tap flex w-full items-stretch gap-2.5 px-3 py-[11px] text-left transition-colors active:bg-black/[0.025]',
    !last && 'border-b border-black/[0.06]'
  );

  const body = (
    <>
      <span className={cn('mt-1 w-[3px] shrink-0 self-stretch rounded-full', accent)} aria-hidden />

      <div className="min-w-0 flex-1">
        <div className="truncate text-[13px] font-extrabold tracking-[-0.01em] text-ink">{round.label}</div>

        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
          {groupLabel ? <span className="text-[11px] font-semibold text-ink2">{groupLabel}</span> : null}
          {rank != null && size > 0 ? (
            <span className="text-[11px] font-medium text-ink3">
              {rank}
              {ordinalSuffix(rank)} of {size}
            </span>
          ) : null}
          <MovementPill promoted={promoted} relegated={relegated} label={movementLabel} />
        </div>

        {why ? (
          <div className="mt-1 truncate text-[10.5px] font-medium text-ink3">{why}</div>
        ) : null}
      </div>

      <div className="flex shrink-0 items-center gap-0.5 self-center pl-1">
        <span
          className={cn(
            'num text-[16px] font-extrabold leading-none tracking-[-0.02em]',
            scorePositive ? 'text-ink' : 'text-ink2'
          )}
        >
          {signedPoints(entry.roundScore ?? 0)}
        </span>
        {tappable ? <ChevronRight width={15} height={15} strokeWidth={2.4} className="text-ink4" /> : null}
      </div>
    </>
  );

  if (tappable) {
    return (
      <button type="button" onClick={() => onOpen!(round.roundId)} className={shell}>
        {body}
      </button>
    );
  }

  return <div className={shell}>{body}</div>;
}

function MovementPill({
  promoted,
  relegated,
  label
}: {
  promoted: boolean;
  relegated: boolean;
  label: string;
}) {
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center gap-[3px] rounded-[6px] px-[7px] py-[2px] text-[10px] font-extrabold uppercase tracking-[0.04em]',
        promoted && 'bg-accent-wash text-accent-ink',
        relegated && 'bg-[rgba(210,84,79,0.12)] text-miss',
        !promoted && !relegated && 'bg-[#EFEFF3] text-ink3'
      )}
    >
      {promoted && <ChevronUp width={10} height={10} strokeWidth={3.2} />}
      {relegated && <ChevronDown width={10} height={10} strokeWidth={3.2} />}
      {label}
    </span>
  );
}
